// 語音播放狀態管理
interface SpeechPlaybackState {
  isPlaying: boolean;
  currentText: string | null;
  service: 'voicevox' | 'browser' | null;
}

// 聲明全局變量
let isVoiceReady = false;
let currentUtterance: SpeechSynthesisUtterance | null = null;

const speechPlaybackState: SpeechPlaybackState = {
  isPlaying: false,
  currentText: null,
  service: null
}

// 播放確認音效
const playClickConfirmSound = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContextClass()
    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()

    // 設置高音正弦波（880Hz）
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime)
    
    // 低音量，短暫持續時間
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1)

    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)
    
    oscillator.start()
    oscillator.stop(audioContext.currentTime + 0.1)
  } catch (error) {
    console.warn('播放確認音效失敗', error)
  }
}

// 雙語速狀態管理 - 確保在客戶端環境初始化
let clickStates: Map<string, 'learning' | 'real'>

// 使用 IIFE 確保在客戶端環境初始化
const initializeClickStates = () => {
  try {
    // 總是創建新的 Map
    clickStates = new Map<string, 'learning' | 'real'>()
    
    // 如果在客戶端環境，添加到 window 對象
    if (typeof window !== 'undefined') {
      ;(window as any).__clickStates = clickStates
    }
    
    // console.log('clickStates 初始化成功:', {
    //   isMap: clickStates instanceof Map,
    //   hasSetMethod: typeof clickStates?.set === 'function',
    //   hasClearMethod: typeof clickStates?.clear === 'function'
    // })
  } catch (error) {
    console.error('clickStates 初始化失敗:', error)
    // 確保即使出錯也創建有效的 Map
    clickStates = new Map<string, 'learning' | 'real'>()
  }
}

// 立即初始化
initializeClickStates()

// 確保 clickStates 始終是 Map 的安全函數
const ensureClickStatesIsMap = () => {
  if (!clickStates || typeof clickStates.clear !== 'function') {
    console.warn('🔧 clickStates 不是有效的 Map，重新初始化')
    initializeClickStates()
  }
}

// 導入語音服務管理器（改為惰性取得，避免匯入即初始化）
import { getTTSServiceManager, UserPreferenceTTSService, RoleBasedTTSService } from './ttsService'

// 導入語音偏好 Hook
// 注意：不再直接使用 Supabase client，改用 API route

// 移除 const supabase = createClient()

// 導入語音配置
import { 
  VOICE_DEFAULTS, 
  isValidVoiceStyle, 
  getSafeVoiceStyle,
  getDefaultvoice_idForGender 
} from '@/config/voiceDefaults'

// 創建安全的默認語音偏好
function createDefaultVoicePreference() {
  return {
    ...VOICE_DEFAULTS,
    timestamp: Date.now()
  }
}

// 驗證語音偏好對象的結構
function validateVoicePreference(pref: any) {
  if (!pref || typeof pref !== 'object') {
    return false
  }
  
  // 檢查必要屬性
  const hasService = typeof pref.service === 'string'
  const hasGender = typeof pref.gender === 'string'
  const hasvoice_id = typeof pref.voice_id === 'number'
  const hasVoiceStyle = typeof pref.voiceStyle === 'string'
  
  return hasService && hasGender && hasvoice_id && hasVoiceStyle
}

// 獲取用戶語音偏好（帶 localStorage 快取）
async function getUserVoicePreference() {
  try {
    // 檢查是否在瀏覽器環境
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      // console.log('非瀏覽器環境，跳過 localStorage 快取')
      return createDefaultVoicePreference()
    }
    
    // 先嘗試從 localStorage 讀取快取（5分鐘內有效）
    const cached = localStorage.getItem('userVoicePreference')
    if (cached) {
      try {
        const data = JSON.parse(cached)
        if (data.timestamp && Date.now() - data.timestamp < 5 * 60 * 1000) {
          // 驗證快取的資料結構
          if (validateVoicePreference(data)) {
            // console.log('🎵 使用快取的語音偏好:', data)
            return data
          } else {
            console.warn('快取的語音偏好結構無效，將重新獲取')
            localStorage.removeItem('userVoicePreference')
          }
        }
      } catch (e) {
        console.warn('快取解析失敗，將從 API 讀取', e)
        localStorage.removeItem('userVoicePreference')
      }
    }

    // 使用 API 讀取語音偏好（避免 RLS 問題）
    const response = await fetch('/api/user/voice-preference', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.warn('API 讀取語音偏好失敗，使用默認值')
      const defaultPref = createDefaultVoicePreference()
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('userVoicePreference', JSON.stringify(defaultPref))
      }
      return defaultPref
    }

    const preference = await response.json()

    // 最終驗證
    if (!validateVoicePreference(preference)) {
      console.error('🚨 API 返回的語音偏好結構無效，使用默認值')
      const defaultPref = createDefaultVoicePreference()
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('userVoicePreference', JSON.stringify(defaultPref))
      }
      return defaultPref
    }

    // console.log('🎵 從 API 讀取語音偏好:', preference)
    
    // 保存到 localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('userVoicePreference', JSON.stringify(preference))
    }
    
    return preference
  } catch (error) {
    console.error('獲取語音偏好失敗:', error)
    const defaultPref = createDefaultVoicePreference()
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('userVoicePreference', JSON.stringify(defaultPref))
    }
    return defaultPref
  }
}

// 初始化語音引擎
const initializeVoice = () => {
  if (isVoiceReady) return Promise.resolve()
  
  return new Promise<void>((resolve) => {
    // 檢查語音引擎是否可用
    if (!('speechSynthesis' in window)) {
      console.warn('瀏覽器不支持語音合成')
      resolve()
      return
    }

    // 等待語音引擎準備就緒
    const checkVoices = () => {
      const voices = window.speechSynthesis.getVoices()
      if (voices.length > 0) {
        isVoiceReady = true
        // console.log('語音引擎已準備就緒，找到', voices.length, '個語音')
        resolve()
      } else {
        // 如果語音列表為空，等待一下再試
        setTimeout(checkVoices, 50)  // 縮短等待時間
      }
    }

    // 立即檢查一次
    checkVoices()
    
    // 監聽語音列表變化
    window.speechSynthesis.onvoiceschanged = () => {
      checkVoices()
    }

    // 設置超時，避免無限等待
    setTimeout(() => {
      if (!isVoiceReady) {
        console.warn('語音引擎初始化超時，繼續使用默認設置')
        isVoiceReady = true
        resolve()
      }
    }, 1500)  // 縮短超時時間
  })
}

// 語音預熱函數 - 解決第一個字消失問題
const warmUpVoice = async () => {
  try {
    // console.log('🔧 執行語音預熱...')
    
    // 縮短預熱文字，減少等待時間
    const warmUpTexts = ['あ']
    
    for (const text of warmUpTexts) {
      const warmUpUtterance = new SpeechSynthesisUtterance(text)
      warmUpUtterance.lang = 'ja-JP'
      warmUpUtterance.volume = 0.001 // 幾乎無聲
      warmUpUtterance.rate = 10 // 極快
      warmUpUtterance.pitch = 1.0
      
      // 等待每個預熱完成
      await new Promise<void>((resolve) => {
        let completed = false
        
        const complete = () => {
          if (!completed) {
            completed = true
            resolve()
          }
        }
        
        warmUpUtterance.onstart = () => {
          // console.log(`🔧 預熱字符: ${text}`)
          // 立即停止並完成
          setTimeout(() => {
            window.speechSynthesis.cancel()
            complete()
          }, 10)
        }
        
        warmUpUtterance.onend = complete
        warmUpUtterance.onerror = complete
        
        // 播放預熱語音
        window.speechSynthesis.speak(warmUpUtterance)
        
        // 最長等待 50ms
        setTimeout(() => {
          window.speechSynthesis.cancel()
          complete()
        }, 50)
      })
      
      // 確保完全停止
      window.speechSynthesis.cancel()
      await new Promise(resolve => setTimeout(resolve, 20))
    }
    
    // console.log('🔧 語音預熱完成')
    
  } catch (error) {
    console.warn('🔧 語音預熱失敗:', error)
  }
}

// JLPT 等級對應的語音設置
const getJLPTVoiceSettings = (level: string, mode: 'learning' | 'real' = 'learning') => {
  const settings = {
    n5: { 
      learning: { rate: 0.7, pitch: 1.0, volume: 0.8 }, // 學習語速：慢速清晰
      real: { rate: 1.1, pitch: 1.0, volume: 0.8 }      // 真實語速：接近考試
    },
    n4: { 
      learning: { rate: 0.8, pitch: 1.0, volume: 0.8 }, // 學習語速：稍慢清晰
      real: { rate: 1.1, pitch: 1.0, volume: 0.8 }      // 真實語速：接近考試
    },
    n3: { 
      learning: { rate: 0.9, pitch: 1.0, volume: 0.8 }, // 學習語速：正常速度
      real: { rate: 1.1, pitch: 1.0, volume: 0.8 }      // 真實語速：接近考試
    },
    n2: { 
      learning: { rate: 1.0, pitch: 1.0, volume: 0.8 }, // 學習語速：標準速度
      real: { rate: 1.1, pitch: 1.0, volume: 0.8 }      // 真實語速：接近考試
    },
    n1: { 
      learning: { rate: 1.1, pitch: 1.0, volume: 0.8 }, // 學習語速：快速流暢
      real: { rate: 1.1, pitch: 1.0, volume: 0.8 }      // 真實語速：接近考試
    },
    default: { 
      learning: { rate: 1.1, pitch: 1.0, volume: 0.8 }, // 默認學習語速
      real: { rate: 1.1, pitch: 1.0, volume: 0.8 }      // 默認真實語速
    }
  }
  
  const levelSettings = settings[level as keyof typeof settings] || settings.default
  return levelSettings[mode]
}

// 修改語音播放函數，使用用戶偏好
const playJapaneseText = async (text: string, level: string = 'default', voiceType: 'male' | 'female' = 'female', options: { 
  onStart?: () => void, 
  onEnd?: () => void, 
  onError?: (error: any) => void,
  role?: string  // 新增角色參數
} = {}) => {
  try {
    // 獲取用戶語音偏好
    let userVoicePreference = await getUserVoicePreference()
    
    // 強化的防護性檢查
    if (!userVoicePreference || typeof userVoicePreference !== 'object') {
      console.error('語音偏好獲取失敗:', userVoicePreference)
      userVoicePreference = createDefaultVoicePreference()
    }
    
    // 確保所有必要屬性都存在且類型正確
    if (typeof userVoicePreference.voiceStyle !== 'string') {
      console.error('🚨 voiceStyle 不是字符串:', typeof userVoicePreference.voiceStyle, userVoicePreference.voiceStyle)
      userVoicePreference.voiceStyle = 'ノーマル'
    }
    
    if (typeof userVoicePreference.service !== 'string') {
      console.error('🚨 service 不是字符串:', typeof userVoicePreference.service)
      userVoicePreference.service = 'voicevox'
    }
    
    if (typeof userVoicePreference.gender !== 'string') {
      console.error('🚨 gender 不是字符串:', typeof userVoicePreference.gender)
      userVoicePreference.gender = 'female'
    }
    
    // ⚠️ 修復：如果 voice_id 不是數字，嘗試轉換或使用預設值 8（與 API 一致）
    if (typeof userVoicePreference.voice_id !== 'number') {
      console.error('🚨 voice_id 不是數字:', typeof userVoicePreference.voice_id, userVoicePreference.voice_id)
      // 嘗試轉換字串為數字
      if (typeof userVoicePreference.voice_id === 'string') {
        const parsed = parseInt(userVoicePreference.voice_id, 10)
        if (!isNaN(parsed)) {
          userVoicePreference.voice_id = parsed
        } else {
          // 轉換失敗，使用預設值 8（與 API 一致，而不是 2）
          userVoicePreference.voice_id = 8
        }
      } else {
        // 其他類型，使用預設值 8（與 API 一致，而不是 2）
        userVoicePreference.voice_id = 8
      }
    }
    
    // 最終驗證
    if (!validateVoicePreference(userVoicePreference)) {
      console.error('🚨 語音偏好驗證失敗，使用默認值')
      userVoicePreference = createDefaultVoicePreference()
    }
  
    // console.log('🎵 用戶語音偏好:', userVoicePreference)

    // 如果當前有正在播放的語音，先停止
    if (speechPlaybackState.isPlaying) {
      stopJapaneseText()
    }

    // 播放確認音效
    playClickConfirmSound()

    // 更新播放狀態
    speechPlaybackState.isPlaying = true
    speechPlaybackState.currentText = text

    // 觸發開始回調
    options.onStart?.()
    
    // 雙語速切換邏輯
    ensureClickStatesIsMap() // 確保 clickStates 是有效的 Map
    const textKey = `${text}_${level}`
    const currentMode = clickStates.get(textKey) || 'learning'
    const nextMode = currentMode === 'learning' ? 'real' : 'learning'
    clickStates.set(textKey, nextMode)
    
    const mode = nextMode

    // 使用用戶偏好的語音服務和性別
    const preferredService = userVoicePreference.service
    const preferredGender = userVoicePreference.gender
    const preferredvoice_id = userVoicePreference.voice_id
    const preferredVoiceStyle = userVoicePreference.voiceStyle

    // console.log(`🎵 語音播放: "${text}" | 等級: ${level} | 模式: ${mode === 'learning' ? '學習語速' : '真實語速'} | 性別: ${preferredGender} | 服務: ${preferredService}`)

    // 特殊處理包含 ';' 的文本
    const parts = text.split(';')
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim()
      
      if (part) {
        // 使用用戶偏好語音服務播放
        const userPreferenceTTS = new UserPreferenceTTSService(getTTSServiceManager().getService(0)!)
        await userPreferenceTTS.speak(part, {
          voice: preferredGender,
          voice_id: preferredvoice_id,
          language: 'ja-JP',
          role: options.role,
          fallbackToBrowser: true,
          level: level  // 傳遞 JLPT 等級
        })
      }
      
      // 只有在 parts 長度大於 1 且不是最後一個部分時，才停頓
      if (parts.length > 1 && i < parts.length - 1) {
        // console.log(`🕒 停頓開始，當前部分: ${part}, 下一部分: ${parts[i+1]}, 停頓時間: 500ms`)
        await new Promise(resolve => setTimeout(resolve, 500))
        // console.log(`🕒 停頓結束`)
      }
    }
    
    // 更新播放狀態
    speechPlaybackState.service = 'voicevox'
    options.onEnd?.()
    
  } catch (error) {
    console.error('🚨 playJapaneseText 執行失敗:', error)
    options.onError?.(error)
  } finally {
    speechPlaybackState.isPlaying = false
    speechPlaybackState.currentText = null
  }
}

// 僅使用瀏覽器語音（不嘗試 Azure TTS），用於聽解等需保證一致環境的情境
const playJapaneseTextBrowser = async (text: string, level: string = 'default', mode: 'learning' | 'real' = 'learning', voiceType: 'male' | 'female' = 'female') => {
  await playWithBrowserTTS(text, level, mode, voiceType)
}

// 停止語音播放
const stopJapaneseText = () => {
  if (speechPlaybackState.isPlaying) {
  // console.log('🛑 停止所有語音播放')
  
    // 停止 Voicevox TTS
    // 移除對 stop 方法的調用
  
  // 停止瀏覽器語音
  if (currentUtterance) {
    window.speechSynthesis.cancel()
    currentUtterance = null
  }
    
    // 清空狀態
    speechPlaybackState.isPlaying = false
    speechPlaybackState.currentText = null
    speechPlaybackState.service = null
  
  // 清空所有雙語速狀態
  ensureClickStatesIsMap() // 確保 clickStates 是有效的 Map
  if (clickStates && typeof clickStates.clear === 'function') {
    clickStates.clear()
  }
  
  // console.log('🛑 語音播放已停止，雙語速狀態已重置')
  }
}

// 檢查語音服務狀態
const getSpeechServiceStatus = async () => {
  const voicevoxAvailable = await getTTSServiceManager().isServiceAvailable()
  const browserAvailable = 'speechSynthesis' in window
  
  // console.log(`🔍 語音服務狀態:`, {
  //   Voicevox: voicevoxAvailable ? '✅ 可用' : '❌ 未配置',
  //   Browser: browserAvailable ? '✅ 可用' : '❌ 不支援',
  //   當前使用: voicevoxAvailable ? 'Voicevox TTS' : 'window.speechSynthesis'
  // })
  
  return {
    voicevoxAvailable: voicevoxAvailable || false,
    browserAvailable,
    currentService: voicevoxAvailable ? 'Voicevox TTS' : 'Browser SpeechSynthesis'
  }
}

// 語音質量評估函數
const assessVoiceQuality = (voice: SpeechSynthesisVoice): number => {
  const nameLowerCase = voice.name.toLowerCase()
  
  // 基於名稱的初步評估
  const qualityScores = {
    japanese: ['japanese', 'ja-jp', 'japan', '日本', 'native'],
    microsoft: ['microsoft', 'azure'],
    google: ['google'],
    synthetic: ['synthetic', 'computer', 'generated']
  }

  let score = 50 // 基礎分數

  // 加分和扣分邏輯
  if (qualityScores.japanese.some(keyword => nameLowerCase.includes(keyword))) {
    score += 20 // 日語相關的語音加分
  }

  if (qualityScores.microsoft.some(keyword => nameLowerCase.includes(keyword))) {
    score += 10 // Microsoft 語音可能質量較好
  }

  if (qualityScores.google.some(keyword => nameLowerCase.includes(keyword))) {
    score += 15 // Google 語音加分
  }

  if (qualityScores.synthetic.some(keyword => nameLowerCase.includes(keyword))) {
    score -= 15 // 明顯的合成語音扣分
  }

  // 語言匹配加分
  if (voice.lang === 'ja-JP') {
    score += 15
  }

  return Math.max(0, Math.min(100, score))
}

// 選擇最佳日文語音（考慮質量）
const selectBestJapaneseVoice = (voices: SpeechSynthesisVoice[], type: 'male' | 'female' = 'female'): SpeechSynthesisVoice | undefined => {
  // 擴展性別關鍵字，增加更多男性和女性語音特徵
  const genderKeywords = {
    male: [
      'Ichiro', 'Keita', 'male', '男', 'man', 'deep', 
      'google japanese male', 'google 日本語 男性',
      // 新增更多男性特徵
      'low pitch', 'masculine', 'strong', 'bass', 
      'male voice', '男性の声', 'male japanese'
    ],
    female: [
      'Ayumi', 'Haruka', 'Sayaka', 'female', '女', 'woman', 'soft', 
      'google japanese female', 'google 日本語 女性',
      // 新增更多女性特徵
      'high pitch', 'gentle', 'light', 
      'female voice', '女性の声', 'female japanese'
    ]
  }

  // 篩選符合性別的語音
  const genderVoices = voices.filter(voice => 
    genderKeywords[type].some(keyword => 
      voice.name.toLowerCase().includes(keyword.toLowerCase())
    )
  )

  // 如果沒有性別匹配的語音，使用所有日語語音
  const candidateVoices = genderVoices.length > 0 ? genderVoices : 
    voices.filter(voice => voice.lang === 'ja-JP')

  // 評估並排序語音
  const rankedVoices = candidateVoices
    .map(voice => ({
      voice,
      quality: assessVoiceQuality(voice)
    }))
    .sort((a, b) => b.quality - a.quality)

  // 記錄語音評估結果
  // console.group('🔊 語音質量評估')
  // console.log(`正在尋找 ${type} 語音`)
  rankedVoices.forEach(({voice, quality}) => {
    // console.log(`${voice.name} (${voice.lang}): 質量得分 ${quality}`)
  })
  // console.groupEnd()

  // 返回質量最高的語音
  return rankedVoices[0]?.voice
}

// 通用角色映射表，僅保留指定角色
const UNIVERSAL_ROLE_VOICE_MAP: { [key: string]: { 
  voice: 'male' | 'female', 
  voice_id?: number,
  voicevoxvoice_id?: number,
  browserVoiceKeywords?: string[],
  alternativeRoles?: string[],
  alternativeStyles?: string[]
} } = {
  // 基礎角色（映射到預定義角色）
  '上司': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    browserVoiceKeywords: ['male', 'deep', 'authoritative'],
    alternativeRoles: ['男性1', '男性2']
  },
  '担当者': { 
    voice: 'female', 
    voice_id: 29,  // No.7 - 女性(較成熟)
    browserVoiceKeywords: ['female', 'clear', 'professional'],
    alternativeRoles: ['女性1', '女性2']
  },
  '同僚': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    browserVoiceKeywords: ['male', 'calm', 'steady'],
    alternativeRoles: ['男性1', '男性2']
  },
  '私': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    browserVoiceKeywords: ['female', 'soft', 'friendly'],
    alternativeRoles: ['女性1', '女性2']
  },
  '先生': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    browserVoiceKeywords: ['male', 'teacher', 'mature'],
    alternativeRoles: ['男性1', '男性2']
  },
  '学生': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    browserVoiceKeywords: ['female', 'young', 'student'],
    alternativeRoles: ['女性1', '女性2']
  },
  '生徒': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    browserVoiceKeywords: ['female', 'young', 'student'],
    alternativeRoles: ['女性1', '女性2']
  },
  '研究者A': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    browserVoiceKeywords: ['male', 'academic', 'professional'],
    alternativeRoles: ['男性1', '男性2']
  },
  '研究者B': { 
    voice: 'female', 
    voice_id: 29,  // No.7 - 女性(較成熟)
    alternativeRoles: ['女性1', '女性2']
  },

  // 預定義角色組（保留指定角色）
  '男性1': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    browserVoiceKeywords: ['male', 'joyful', 'happy', 'excited'],
    voicevoxvoice_id: 83,
    alternativeStyles: ['喜び', '熱血', 'しっとり', '不機嫌', 'かなしみ', '囁き']
  },
  '男性2': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    browserVoiceKeywords: ['male', 'young', 'energetic'],
    voicevoxvoice_id: 11,
    alternativeStyles: ['喜び', 'ツンギレ', '悲しみ']
  },
  '女性1': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    browserVoiceKeywords: ['female', 'soft', 'young'],
    voicevoxvoice_id: 8
  },
  '女性2': { 
    voice: 'female', 
    voice_id: 29,  // No.7 - 女性(較成熟)
    browserVoiceKeywords: ['female', 'clear', 'mature'],
    voicevoxvoice_id: 29
  },

  // 備用角色
  '備用': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    browserVoiceKeywords: ['female', 'young'],
    voicevoxvoice_id: 8
  },

  // 新增的角色映射
  '店員': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    voicevoxvoice_id: 83,
    browserVoiceKeywords: ['male', 'service', 'professional'],
    alternativeRoles: ['男性1', '男性2']
  },
  '客': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'customer', 'inquiring'],
    alternativeRoles: ['男性1', '男性2']
  },

  // 角色映射
  '山田さん': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'enthusiastic', 'ambitious', 'learning', 'active', 'casual', 'friendly'],
    alternativeRoles: ['男性1', '男性2']
  },
  '田中さん': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'casual', 'friendly']
  },
  '佐藤さん': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'soft', 'friendly']
  },
  '鈴木さん': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'young', 'student']
  },
  '高橋さん': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'serious', 'studious']
  },
  '山本さん': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'cheerful', 'learning']
  },
  '相手': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'responsive', 'casual']
  },

  // 角色映射
  '友人': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    voicevoxvoice_id: 83,
    browserVoiceKeywords: ['male', 'friendly', 'casual', 'enthusiastic'],
    alternativeRoles: ['男性1', '男性2']
  },
  '母': { 
    voice: 'female', 
    voice_id: 16,  // 九州そら
    voicevoxvoice_id: 16,
    browserVoiceKeywords: ['female', 'mother', 'caring', 'warm'],
    alternativeRoles: ['女性1', '女性2']
  },
  '父': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'father', 'authoritative', 'mature'],
    alternativeRoles: ['男性1', '男性2']
  },

  // 新增角色映射
  'A': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'speaker', 'person'],
    alternativeRoles: ['男性1', '男性2']
  },
  'B': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'speaker', 'person'],
    alternativeRoles: ['女性1', '女性2']
  },
  '友人A': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    voicevoxvoice_id: 83,
    browserVoiceKeywords: ['male', 'friend', 'casual'],
    alternativeRoles: ['男性1', '男性2']
  },
  '友人B': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'friend', 'casual'],
    alternativeRoles: ['女性1', '女性2']
  },
  '駅員': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    voicevoxvoice_id: 83,
    browserVoiceKeywords: ['male', 'staff', 'professional'],
    alternativeRoles: ['男性1', '男性2']
  },

  // 新增角色映射
  '受付': { 
    voice: 'female', 
    voice_id: 29,  // No.7 - 女性(較成熟)
    voicevoxvoice_id: 29,
    browserVoiceKeywords: ['female', 'reception', 'professional'],
    alternativeRoles: ['女性1', '女性2']
  },
  '来訪者': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'visitor', 'guest'],
    alternativeRoles: ['男性1', '男性2']
  },
  '人事担当': { 
    voice: 'female', 
    voice_id: 29,  // No.7 - 女性(較成熟)
    voicevoxvoice_id: 29,
    browserVoiceKeywords: ['female', 'hr', 'professional'],
    alternativeRoles: ['女性1', '女性2']
  },
  '異動者': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'transferee', 'employee'],
    alternativeRoles: ['男性1', '男性2']
  },
  '顧客': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'customer', 'client'],
    alternativeRoles: ['男性1', '男性2']
  },
  '医師': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'doctor', 'medical'],
    alternativeRoles: ['男性1', '男性2']
  },
  '患者': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'patient', 'medical'],
    alternativeRoles: ['女性1', '女性2']
  },
  '研究者': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'researcher', 'academic'],
    alternativeRoles: ['男性1', '男性2']
  },
  '編集長': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'editor', 'chief'],
    alternativeRoles: ['男性1', '男性2']
  },
  '記者': { 
    voice: 'female', 
    voice_id: 8,   // 春日部つむぎ - 女性(較年輕)
    voicevoxvoice_id: 8,
    browserVoiceKeywords: ['female', 'reporter', 'journalist'],
    alternativeRoles: ['女性1', '女性2']
  },
  'プロジェクトマネージャー': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'manager', 'project'],
    alternativeRoles: ['男性1', '男性2']
  },
  '営業担当': { 
    voice: 'male', 
    voice_id: 83,  // 青山龍星-喜び - 普通商業角色
    voicevoxvoice_id: 83,
    browserVoiceKeywords: ['male', 'sales', 'business'],
    alternativeRoles: ['男性1', '男性2']
  },
  '社員': { 
    voice: 'male', 
    voice_id: 11,  // 玄野武宏 - 年輕男性
    voicevoxvoice_id: 11,
    browserVoiceKeywords: ['male', 'employee', 'staff'],
    alternativeRoles: ['男性1', '男性2']
  },
  '保険代理店': { 
    voice: 'female', 
    voice_id: 29,  // No.7 - 女性(較成熟)
    voicevoxvoice_id: 29,
    browserVoiceKeywords: ['female', 'insurance', 'agent'],
    alternativeRoles: ['女性1', '女性2']
  },
  '金融アドバイザー': { 
    voice: 'male', 
    voice_id: 13,  // 青山龍星 - 嚴肅角色、中年男性
    voicevoxvoice_id: 13,
    browserVoiceKeywords: ['male', 'advisor', 'financial'],
    alternativeRoles: ['男性1', '男性2']
  }
}

// 遞歸查找角色的公共函數
function findRoleInfo(role: string): { 
  voice: 'male' | 'female', 
  voice_id?: number,
  browserVoiceKeywords?: string[]
} {
  // 精確匹配角色
  if (UNIVERSAL_ROLE_VOICE_MAP[role]) {
    // console.log(`✅ 精確匹配角色: "${role}"`)
    return {
      voice: UNIVERSAL_ROLE_VOICE_MAP[role].voice,
      voice_id: UNIVERSAL_ROLE_VOICE_MAP[role].voice_id || UNIVERSAL_ROLE_VOICE_MAP[role].voicevoxvoice_id,
      browserVoiceKeywords: UNIVERSAL_ROLE_VOICE_MAP[role].browserVoiceKeywords
    }
  }

  // 嘗試部分匹配
  const partialMatches = Object.keys(UNIVERSAL_ROLE_VOICE_MAP).filter(
    key => role.includes(key)
  )

  if (partialMatches.length > 0) {
    const matchedRole = partialMatches[0]
    // console.log(`🔎 部分匹配角色: "${role}" → "${matchedRole}"`)
    return {
      voice: UNIVERSAL_ROLE_VOICE_MAP[matchedRole].voice,
      voice_id: UNIVERSAL_ROLE_VOICE_MAP[matchedRole].voice_id || UNIVERSAL_ROLE_VOICE_MAP[matchedRole].voicevoxvoice_id,
      browserVoiceKeywords: UNIVERSAL_ROLE_VOICE_MAP[matchedRole].browserVoiceKeywords
    }
  }
  
  // 最終備用
  console.warn(`⚠️ 未找到角色映射，使用備用角色: "${role}"`)
  return UNIVERSAL_ROLE_VOICE_MAP['備用']
}

// 文本分段工具函數
function splitTextByLength(text: string, maxLength: number = 150): string[] {
  if (text.length <= maxLength) {
    return [text]
  }

  const segments: string[] = []
  const sentences = text.split(/[。！？]/).filter(s => s.trim())
  
  let currentSegment = ''
  
  for (const sentence of sentences) {
    const trimmedSentence = sentence.trim()
    if (!trimmedSentence) continue
    
    // 如果當前段落加上新句子會超過限制
    if (currentSegment && (currentSegment + trimmedSentence).length > maxLength) {
      segments.push(currentSegment.trim())
      currentSegment = trimmedSentence
    } else {
      currentSegment += (currentSegment ? '。' : '') + trimmedSentence
    }
  }
  
  // 添加最後一個段落
  if (currentSegment.trim()) {
    segments.push(currentSegment.trim())
  }
  
  return segments
}

// 生成用戶會話 ID
function generateUserSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// 解析對話，忽略角色名稱，支持多級備用
function parseDialogue(script: string): { 
  dialogueParts: Array<{
    role: string, 
    text: string,
    voice: 'male' | 'female', 
    voice_id?: number,
    browserVoiceKeywords?: string[]
  }>, 
  characters: Array<{
    role: string, 
    voice: 'male' | 'female', 
    voice_id?: number,
    browserVoiceKeywords?: string[]
  }>
} {
  // console.group('🔍 對話解析詳情')
  // console.log('原始腳本:', script)

  // 更嚴格的角色名稱解析正則表達式
  const dialogueParts = script.split('\n')
    .map(part => {
      // 更複雜的正則表達式，處理更多角色名稱格式
      const roleRegex = /^([^:：]+)[:：]\s*(.+)$/
      const match = part.match(roleRegex)
      
      if (match) {
        const [, roleName, dialogue] = match
        // console.log(`🏷️ 檢測到角色: "${roleName}"`)
        // console.log(`💬 原始對話: "${part}"`)
        // console.log(`🗣️ 提取對話: "${dialogue.trim()}"`)
        return { 
          role: roleName.trim(), 
          text: dialogue.trim() 
        }
      }
      
      return { 
        role: '備用', 
        text: part.trim() 
      }
    })
    .filter(part => part.text) // 移除空白對話

  // console.log('解析後對話部分:', dialogueParts)

  // 為每個對話部分找到對應的語音配置
  const characters = dialogueParts.map(part => {
    const roleInfo = findRoleInfo(part.role)
    
    return {
      role: part.role,
      ...roleInfo
    }
  })

  // console.log('最終角色映射:', characters)
  // console.groupEnd()

  return { 
    dialogueParts: dialogueParts.map(part => ({
      role: part.role,
      text: part.text,
      ...findRoleInfo(part.role)
    })), 
    characters 
  }
}

// 支持自動解析的多角色對話播放方法（整合角色映射功能）
async function playDialogueScript(
  script: string, 
  options: {
    voiceService?: 'browser' | 'voicevox',
    useRoleMapping?: boolean,  // 新增：是否使用角色映射（模擬考試用）
    onProgress?: (current: number, total: number) => void,  // 新增：進度回調
    userSessionId?: string,  // 新增：用戶會話 ID
    question?: string  // 新增：題目導入（聽解考題用）
  } = {}
): Promise<void> {
  const { 
    voiceService = 'voicevox',  // 預設使用 Voicevox
    useRoleMapping = false,     // 預設不使用角色映射（學習頁面用）
    onProgress,
    userSessionId = generateUserSessionId(),
    question
  } = options

  // console.group('🎙️ 對話播放詳情')
  // console.log('原始對話腳本:', script)
  // console.log('使用角色映射:', useRoleMapping)
  // console.log('用戶會話 ID:', userSessionId)

  const { dialogueParts } = parseDialogue(script)

  // console.log('解析後的對話部分:', dialogueParts)

  // 檢查 VOICEVOX 可用性
  const ttsManager = getTTSServiceManager()
  const isVoicevoxAvailable = await ttsManager.isServiceAvailableCached(0)
  
  // 決定實際使用的語音服務
  let actualVoiceService = voiceService
  if (!isVoicevoxAvailable && voiceService === 'voicevox') {
    console.warn(`⚠️ VOICEVOX 不可用，回退到瀏覽器語音`)
    actualVoiceService = 'browser'
  }

  // 移除並發預熱，確保嚴格串行播放
  // console.log(`🎵 開始串行播放 ${dialogueParts.length} 個對話部分`)

  // 串行播放（確保順序正確）
  for (let i = 0; i < dialogueParts.length; i++) {
    const part = dialogueParts[i]
    // console.log(`🔊 當前播放部分 - 角色: ${part.role}, 文本: ${part.text}`)

    try {
      // 直接播放完整文本，不分段
      // console.log(`🎵 播放完整文本: ${part.text}`)
      
      // 更新進度
      if (onProgress) {
        onProgress(i * 100, dialogueParts.length * 100)
      }
      
      // 根據配置選擇語音服務
      if (useRoleMapping) {
        // 使用角色映射服務（模擬考試用）
        const roleBasedService = new RoleBasedTTSService(getTTSServiceManager().getService(0)!)
        await roleBasedService.speak(part.text, {
          voice: part.voice,
          voice_id: part.voice_id,
          language: 'ja-JP',
          role: part.role,
          userSessionId: userSessionId,
          skipDialogueParsing: true  // 跳過對話解析，避免重複解析
        })
      } else {
        // 使用用戶偏好服務（學習頁面用）
        const userPreferenceService = new UserPreferenceTTSService(getTTSServiceManager().getService(0)!)
        await userPreferenceService.speak(part.text, {
          voice: part.voice,
          language: 'ja-JP',
          userSessionId: userSessionId
        })
      }

      // 只在不是最後一句時添加停頓
      if (i < dialogueParts.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50))
      }
    } catch (error) {
      console.error(`🚨 播放角色 ${part.role} 的對話時出錯`, error)
    }
  }

  // 如果有題目，在對話播放完成後播放題目導入
  if (question) {
    // console.log('📝 播放題目導入:', question)
    
    // 停頓1秒
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // 播放題目（使用第一個角色的語音）
    try {
      if (useRoleMapping && dialogueParts.length > 0) {
        // 使用第一個角色的語音播放題目
        const firstRole = dialogueParts[0]
        // console.log(`🎭 使用第一個角色 "${firstRole.role}" 播放題目導入`)
        
        const roleBasedService = new RoleBasedTTSService(getTTSServiceManager().getService(0)!)
        await roleBasedService.speak(question, {
          voice: firstRole.voice,
          voice_id: firstRole.voice_id,
          language: 'ja-JP',
          role: firstRole.role,
          userSessionId: userSessionId,
          skipDialogueParsing: true
        })
      } else {
        // 回退到預設語音
        const userPreferenceService = new UserPreferenceTTSService(getTTSServiceManager().getService(0)!)
        await userPreferenceService.speak(question, {
          voice: 'female',
          language: 'ja-JP',
          userSessionId: userSessionId
        })
      }
    } catch (error) {
      console.error('🚨 播放題目導入時出錯', error)
    }
  }

  // 播放完成提示音
  try {
    playClickConfirmSound()
    // console.log('🎵 對話播放完成')
  } catch (error) {
    console.error('🚨 播放完成提示音時出錯', error)
  }

  console.groupEnd()
}

// 播放單個文本段落的輔助函數
async function playTextSegment(
  text: string,
  part: {
    role: string,
    voice: 'male' | 'female',
    voice_id?: number,
    browserVoiceKeywords?: string[]
  },
  voiceService: 'browser' | 'voicevox',
  useRoleMapping: boolean,
  userSessionId: string
): Promise<void> {
  try {
    if (voiceService === 'voicevox' && part.voice_id) {
      if (useRoleMapping) {
        // 模擬考試：使用角色映射語音服務
        // console.log(`🎭 使用角色映射播放 - 語音ID: ${part.voice_id}, 性別: ${part.voice}`)
        const roleBasedTTS = new RoleBasedTTSService(getTTSServiceManager().getService(0)!)
        await roleBasedTTS.speak(text, {
          voice: part.voice,
          voice_id: part.voice_id,
          language: 'ja-JP',
          role: part.role,
          fallbackToBrowser: false, // 不允許回退到瀏覽器語音
          userSessionId: userSessionId,
          skipDialogueParsing: true  // 跳過對話解析，避免重複播放
        })
      } else {
        // 學習頁面：使用用戶偏好語音服務
        // console.log(`🎵 使用用戶偏好播放 - 語音ID: ${part.voice_id}, 性別: ${part.voice}`)
        const userPreferenceTTS = new UserPreferenceTTSService(getTTSServiceManager().getService(0)!)
        await userPreferenceTTS.speak(text, {
          voice: part.voice,
          voice_id: part.voice_id,
          language: 'ja-JP',
          role: part.role,
          fallbackToBrowser: false, // 不允許回退到瀏覽器語音
          userSessionId: userSessionId
        })
      }
    } else if (voiceService === 'browser') {
      // 只有明確選擇瀏覽器語音時才使用
      // console.log(`🌐 使用瀏覽器語音 - 性別: ${part.voice}`)
      await playJapaneseTextBrowser(
        text, 
        'test_speech', 
        'real', 
        part.voice
      )
    } else {
      throw new Error(`不支援的語音服務: ${voiceService}`)
    }
  } catch (error) {
    console.error(`🚨 播放文本段落時出錯: ${text}`, error)
    throw error
  }
}

// 瀏覽器語音播放（修復第一個字消失問題）
const playWithBrowserTTS = async (text: string, level: string = 'default', mode: 'learning' | 'real' = 'learning', voiceType: 'male' | 'female' = 'female') => {
  try {
    // console.log('開始播放瀏覽器語音:', text, '等級:', level, '模式:', mode, '性別:', voiceType)
    
    // 確保語音引擎已準備就緒
    await initializeVoice()
    
    // 進行語音預熱（解決第一個字消失問題）
    await warmUpVoice()
    
    // 完全停止所有語音
    window.speechSynthesis.cancel()
    
    // 等待更長時間確保停止完成
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // 獲取可用語音
    const voices = window.speechSynthesis.getVoices()
    // console.log('可用語音數量:', voices.length)
    
    // 根據當前播放的語音類型選擇語音
    const japaneseVoice = selectBestJapaneseVoice(voices, voiceType)
    
    if (japaneseVoice) {
      // console.log('使用語音:', japaneseVoice.name, '語言:', japaneseVoice.lang)
    } else {
      console.warn(`未找到 ${voiceType} 日語語音，將使用默認語音`)
      // console.log('可用語音列表:', voices.map(v => `${v.name} (${v.lang})`))
    }
    
    // 獲取對應等級和模式的語音設置
    const voiceSettings = getJLPTVoiceSettings(level, mode)
    // console.log('使用語音設置:', voiceSettings, '模式:', mode)
    
    // 創建新的語音實例 - 使用更複雜的第一個字保護策略
    // 1. 在文字前加靜音字符和空格
    const enhancedText = `\u200B ${text}` // 使用零寬空格 + 空格
    
    // 創建兩個語音實例：預熱和實際播放
    const preWarmUtterance = new SpeechSynthesisUtterance('\u200B')
    const mainUtterance = new SpeechSynthesisUtterance(enhancedText)
    
    // 設置預熱語音參數
    preWarmUtterance.lang = 'ja-JP'
    preWarmUtterance.volume = 0.001
    preWarmUtterance.rate = 10
    
    // 設置主語音參數
    mainUtterance.lang = 'ja-JP'
    // 使用對應等級的語音參數
    mainUtterance.rate = voiceSettings.rate
    mainUtterance.pitch = voiceType === 'male' ? 0.6 : voiceSettings.pitch
    mainUtterance.volume = voiceSettings.volume
    
    if (japaneseVoice) {
      preWarmUtterance.voice = japaneseVoice
      mainUtterance.voice = japaneseVoice
      // console.log('✅ 使用日語語音:', japaneseVoice.name)
    } else {
      console.warn('⚠️ 未找到日語語音，將使用系統默認語音（可能不是日語）')
      // 強制設置語言為日語，即使沒有日語語音引擎
      preWarmUtterance.lang = 'ja-JP'
      mainUtterance.lang = 'ja-JP'
    }
    
    // 創建 Promise 來控制播放流程
    return new Promise<void>((resolve, reject) => {
      // 設置主語音的回調
      mainUtterance.onend = () => {
        // console.log('語音播放完成')
        resolve()
      }
      
      mainUtterance.onerror = (event) => {
        console.warn('語音播放錯誤:', event.error)
        reject(event.error)
      }
      
      // 先播放預熱語音，然後播放主語音
      window.speechSynthesis.speak(preWarmUtterance)
      
      // 短暫延遲後播放主語音
      setTimeout(() => {
        window.speechSynthesis.speak(mainUtterance)
      }, 50)
    })
  } catch (error) {
    console.warn('瀏覽器語音播放失敗:', error)
    throw error
  }
}

// 獲取角色圖片的函數，支持不同模式
function getRoleAvatar(role: string, style: string = 'default'): { 
  path: string, 
  hasAlternativeStyles?: boolean 
} {
  const baseStyles = ['default', 'happy', 'angry', 'sad', 'calm', 'excited'];
  const basePath = `/images/roles/aoyama_ryusei/jinya_ren`;
  
  // 檢查特定風格的圖片是否存在
  const stylePath = `${basePath}/jinya_ren_${style}.png`;
  const defaultPath = `${basePath}/jinya_ren.png`;

  // 這裡可以添加邏輯檢查文件是否真實存在
  // 暫時簡化處理
  return {
    path: baseStyles.includes(style) ? stylePath : defaultPath,
    hasAlternativeStyles: baseStyles.length > 1
  };
}

// 更新 ROLE_CUSTOM_INFO
const ROLE_CUSTOM_INFO: { [key: string]: {
  displayName?: string,
  avatarPath?: string,
  description?: string,
  getAvatar?: (style?: string) => { path: string, hasAlternativeStyles?: boolean }
} } = {
  '神谷蓮': { 
    displayName: '神谷蓮',
    description: '使用青山龍星語音的自定義角色',
    getAvatar: (style) => getRoleAvatar('神谷蓮', style)
  },
  // 其他角色保持不變
}

// 獲取角色自定義信息的函數
function getRoleCustomInfo(role: string) {
  return ROLE_CUSTOM_INFO[role] || {
    displayName: role,
    avatarPath: '/images/roles/default.png',
    description: '預設角色'
  }
}

// 在需要使用自定義信息的地方調用此函數
// 例如在 parseDialogue 或其他相關函數中

// 導出所有需要的方法和常量
export { 
  playJapaneseText,
  playJapaneseTextBrowser,
  stopJapaneseText,
  getSpeechServiceStatus,
  
  playDialogueScript, 
  parseDialogue,
  UNIVERSAL_ROLE_VOICE_MAP,
  playClickConfirmSound,
  getUserVoicePreference  // 導出用戶語音偏好獲取函數
}


